import { defineStore } from 'pinia';
import * as api from "../services/api";
import {useStepEditStore} from "./StepEditStore";
import {usePresenceStore} from "./PresenceStore";
import {useCampaignStore} from "./CampaignStore";
import {t} from "@/i18n";

const EDIT_REQUEST_SUPERSEDED = 'edit_request_superseded';
const STEP_EDIT_LOCKED = 'step_edit_locked';

export function isHandledStepEditError(error) {
    return [
        EDIT_REQUEST_SUPERSEDED,
        STEP_EDIT_LOCKED,
        'session_expired',
        'fresh_session_required',
        'editor_idle_timeout',
    ].includes(error?.reason);
}

function supersededEditRequest() {
    const error = new Error(t('schedule.steps_store.edit_superseded'));
    error.reason = EDIT_REQUEST_SUPERSEDED;

    return error;
}

function lockedStepEditRequest(presenceStore, uuid) {
    const error = new Error(t('schedule.steps_store.edit_locked'));
    error.reason = STEP_EDIT_LOCKED;
    error.user = presenceStore.getFocusUser(uuid) ?? null;

    return error;
}

export const useStepsStore = defineStore('StepsStore', {
    state: () => {
        return {
            all: {},
            loading: true,
            editingUuid: null,
            showEditDialog: false,
            preview_url: null,
            loadingStep: false,
            last_edited_step: null,
            _editRequestSequence: 0,
        };
    },
    getters: {
        byID: (state) => {
            return (id) => state.all[id];
        },
        showPreviewDialog: (state) => state.preview_url !== null,
    },
    actions: {
        /**
         * create a new Step on the server for the given Campaign and App
         *
         * @param payload {campaign_id, app_id, field, index}
         * @returns {*}
         */
        createOnServer(payload) {
            return new Promise((resolve, reject) => {
                const campaignStore = useCampaignStore();

                const url = window.route('steps.store');

                api.client.post(url, {
                    ...payload,
                    schedule_base_revision: payload.schedule_base_revision ?? campaignStore.getRevision('schedule'),
                }).then(response => {
                    const data = response.data;
                    const step = data.step || data;

                    this.all[step.uuid] = step;

                    this.applyScheduleRevisionFromResponse(data);

                    resolve(data);
                }).catch(error => {
                    if (this.handleRequestError(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    reject(error.response?.data || error);
                });
            });
        },
        replicateOnServer(payload) {
            return new Promise((resolve, reject) => {
                const campaignStore = useCampaignStore();

                const url = window.route('steps.replicate', {
                    step: payload.uuid,
                    index: payload.index,
                    schedule_base_revision: payload.schedule_base_revision ?? campaignStore.getRevision('schedule'),
                });

                api.client.get(url).then(response => {
                    const data = response.data;
                    const step = data.step || data;

                    this.all[step.uuid] = step;

                    if (data.schedule_revision !== undefined) {
                        campaignStore.setRevision('schedule', data.schedule_revision);
                    }

                    if (data.schedule) {
                        campaignStore.campaign.schedule = data.schedule;
                    }

                    resolve(data);
                }).catch(error => {
                    if (this.handleRequestError(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    reject(error.response?.data || error);
                });
            });
        },
        deleteOnServer(payload) {
            return new Promise((resolve, reject) => {
                const campaignStore = useCampaignStore();

                const url = window.route('steps.destroy', {
                    step: payload.uuid,
                    field: payload.field,
                    index: payload.index,
                });

                api.client.delete(url, {
                    data: {
                        schedule_base_revision: payload.schedule_base_revision ?? campaignStore.getRevision('schedule'),
                        legals_base_revision: payload.legals_base_revision ?? campaignStore.getRevision('legals'),
                    }
                }).then(response => {
                    const data = response.data;

                    if (data.uuid) {
                        delete this.all[data.uuid];
                    }

                    if (data.schedule_revision !== undefined) {
                        campaignStore.setRevision('schedule', data.schedule_revision);
                    }

                    if (data.legals_revision !== undefined) {
                        campaignStore.setRevision('legals', data.legals_revision);
                    }

                    if (data.schedule) {
                        campaignStore.campaign.schedule = data.schedule;
                    }

                    if (data.legals) {
                        campaignStore.campaign.legals = data.legals;
                    }

                    resolve(data);
                }).catch(error => {
                    if (this.handleRequestError(error)) {
                        reject(error.response?.data || error);
                        return;
                    }

                    reject(error.response?.data || error);
                });
            });
        },
        async editStep(uuid) {
            const campaignStore = useCampaignStore();
            const presenceStore = usePresenceStore();
            campaignStore.closeSnackbar();

            // Zentraler Guard für Karten-, Menü- und Next-App-Aufrufe.
            if (presenceStore.isFocused(uuid)) {
                const error = lockedStepEditRequest(presenceStore, uuid);
                campaignStore.snackbarText = error.user?.fullName
                    ? t('schedule.steps_store.edit_locked_by', {name: error.user.fullName})
                    : error.message;
                campaignStore.showSnackbar = true;
                throw error;
            }

            const requestSequence = ++this._editRequestSequence;
            this.loadingStep = true;
            this.editingUuid = uuid;
            this.last_edited_step = null;
            this.showEditDialog = true;

            const stepEditStore = useStepEditStore();

            /**
             * Der StepEditor bleibt nach dem ersten Öffnen im Vuetify-Dialog
             * montiert und hält genau diese Store-Instanz. `$dispose()` würde
             * sie aus Pinia entfernen; ein späterer Aufruf von `editStep()`
             * erzeugte dann eine zweite Instanz, während der sichtbare Editor
             * weiterhin die erste beobachtet. Das fiel besonders beim Button
             * "Nachfolgende App bearbeiten" auf: Request und Vorschau
             * wechselten, das Formular aber nicht zuverlässig.
             *
             * Die eine langlebige Instanz wird nach erfolgreichem Laden unten
             * zurückgesetzt und mit dem neuen Schritt befüllt.
             */

            const url = window.route('steps.edit', {step: uuid});

            try {
                const response = await api.client.get(url);

                // Ein langsamer älterer Request darf den inzwischen gewählten
                // Editor nicht wieder zurückschalten.
                if (requestSequence !== this._editRequestSequence) {
                    throw supersededEditRequest();
                }

                // Während der Request lief, kann ein anderer Nutzer den Step
                // geöffnet haben. Vor dem Store-Patch erneut prüfen.
                if (presenceStore.isFocused(uuid)) {
                    throw lockedStepEditRequest(presenceStore, uuid);
                }

                stepEditStore.reset();
                stepEditStore.$patch(response.data);
                stepEditStore.parseEditorConfig();
                stepEditStore.parseInfoEditor();

                // Erst der vollständig normalisierte Stand ist der Ausgangs-
                // punkt für die Dirty-State-Messung.
                stepEditStore.captureOriginal();

                return response.data;
            } catch (error) {
                if (requestSequence !== this._editRequestSequence
                    || error?.reason === EDIT_REQUEST_SUPERSEDED) {
                    throw supersededEditRequest();
                }

                if (error?.reason === STEP_EDIT_LOCKED) {
                    campaignStore.snackbarText = error.user?.fullName
                        ? t('schedule.steps_store.edit_locked_by', {name: error.user.fullName})
                        : error.message;
                    campaignStore.showSnackbar = true;
                    this.showEditDialog = false;
                    this.editingUuid = null;
                    throw error;
                }

                if (this.handleRequestError(error)) {
                    throw error.response?.data || error;
                }

                const message = error.response?.data?.message;

                if (message) {
                    campaignStore.snackbarText = message;
                    campaignStore.showSnackbar = true;
                }

                this.showEditDialog = false;
                this.editingUuid = null;

                throw error.response?.data || error;
            } finally {
                if (requestSequence === this._editRequestSequence) {
                    this.loadingStep = false;
                }
            }
        },
        closeStepEdit() {
            usePresenceStore().fieldBlurred(this.editingUuid);
            this.loadingStep = false;
            this.last_edited_step = this.editingUuid;
            this.editingUuid = null;
            this.showEditDialog = false;
        },
        updateStepInStore(updatedStep) {
            this.all[updatedStep.uuid] = updatedStep;
        },
        /**
         * update the editor_config of the given app
         *
         * @returns {*}
         */
        updateEditorConfig() {
            const stepEditStore = useStepEditStore();
            const app_id = this.all[this.editingUuid].app_id;
            const url = window.route('applications.updateEditorConfig', {application: app_id});
            const payload = {
                editor_config: stepEditStore.editor_config
            };

            return new Promise((resolve, reject) => {
                api.client.patch(url, payload)
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch(error => {
                        if (this.handleRequestError(error)) {
                            reject(error.response?.data || error);
                            return;
                        }

                        reject(error.response?.data || error);
                    });
            });
        },
        /**
         * update the info_editor of the given app
         *
         * @returns {*}
         */
        updateInfoEditor() {
            const stepEditStore = useStepEditStore();
            const app_id = this.all[this.editingUuid].app_id;
            const url = window.route('applications.updateInfoEditor', {application: app_id});
            const payload = {
                info_editor: stepEditStore.info_editor
            };

            return new Promise((resolve, reject) => {
                api.client.patch(url, payload)
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch(error => {
                        if (this.handleRequestError(error)) {
                            reject(error.response?.data || error);
                            return;
                        }

                        reject(error.response?.data || error);
                    });
            });
        },
        /**
         * received Step Created Event
         * - someone else has just created a step!
         *
         * @param event
         */
        receivedStepCreated(event) {
            const campaignStore = useCampaignStore();

            this.all[event.step.uuid] = event.step;

            if (event.schedule) {
                campaignStore.campaign.schedule = event.schedule;
            }

            if (event.schedule_revision !== undefined) {
                campaignStore.setRevision('schedule', event.schedule_revision);
            }
        },

        /**
         * received Main Step Created Event
         * - someone else just changed/created the main step
         *
         * @param event
         */
        receivedMainStepCreated(event) {
            // Event has the new step and the updated campaign schedule
            this.all[event.step.uuid] = event.step;
            // Update Campaign Schedule
            useCampaignStore().campaign.main = event.main;
        },

        /**
         * received Step Destroyed Event
         * - someone else has just deleted a step!
         *
         * @param event
         */
        receivedStepDestroyed(event) {
            const campaignStore = useCampaignStore();

            delete this.all[event.uuid];

            if (event.schedule) {
                campaignStore.campaign.schedule = event.schedule;
            }

            if (event.schedule_revision !== undefined) {
                campaignStore.setRevision('schedule', event.schedule_revision);
            }
        },

        handleRequestError(error) {
            const campaignStore = useCampaignStore();

            if (campaignStore.handleExpiredEditorSession(error)) {
                return true;
            }

            if (campaignStore.handleStaleRevision(error)) {
                return true;
            }

            console.error(error);
            return false;
        },

        applyScheduleRevisionFromResponse(data) {
            const campaignStore = useCampaignStore();

            const revision = data?.schedule_revision
                ?? data?.revision
                ?? data?.change_revisions?.schedule;

            if (revision !== undefined) {
                campaignStore.setRevision('schedule', revision);
            }

            if (data?.schedule) {
                campaignStore.campaign.schedule = data.schedule;
            }
        },

        // ----------------------------------------------------------------------------
        // Channel Events
        // ----------------------------------------------------------------------------
        bindChannelEvents(channel_name) {
            echoInstance.join(channel_name)
                .listen('StepCreated', this.receivedStepCreated)
                .listen('MainStepCreated', this.receivedMainStepCreated)
                .listen('StepDestroyed', this.receivedStepDestroyed);
        },

        unbindChannelEvents(channel_name) {
            echoInstance.join(channel_name)
                .stopListening('StepCreated', this.receivedStepCreated)
                .stopListening('MainStepCreated', this.receivedMainStepCreated)
                .stopListening('StepDestroyed', this.receivedStepDestroyed);
        },
    }
});
